import { useEffect, useState } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { useReselector } from '@/utils/sharedHooks'
import { useIsTaxSeasonOpen } from '@/features/taxes/annual-taxes/useIsTaxSeasonOpen'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'
import {
  getAnnualTaxFilingForYearSelector,
  selectLaterJoinerOptedOutDuringSignup,
  selectIsEligibleForCurrentYearTaxServices,
  selectAnnualTaxNotAvailable,
} from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAnnualTaxFormTypesIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFormType.slice'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { fetchTaxUserDocumentsIfNeeded } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { fetchAllEoyReviewStepsIfNeeded } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/allEoyReviewSteps.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { fetchUserEoyReviewProgress } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/userEndOfYearReviewProgress.slice'

export enum AnnualTaxOverviewPage {
  loading,
  upgradeToPremium,
  upgradeToPremiumOffSeason,
  disqualified,
  yearEndHome,
  lateJoinerOptedOut,
  nextYearTaxCenter,
  freeTrialAnnualTaxPreview,
  annualTaxTskEntryPoint,
  taxSeasonNotOpen,
}

export const useAnnualTaxOverviewPage = () => {
  const dispatch = useAppDispatch()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const [fetching, setFetching] = useState(true)
  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        dispatch(fetchAnnualTaxFormTypesIfNeeded()),
        dispatch(fetchUserDocuments()),
        dispatch(fetchTaxUserDocumentsIfNeeded()),
        dispatch(fetchAllAnnualTaxDetailsIfNeeded()),
        dispatch(fetchSubscriptions()),
        dispatch(fetchUserDocumentCategoriesIfNeeded()),
        dispatch(fetchAllEoyReviewStepsIfNeeded()),
        // Force refetch of these because form needs and submissions may have happened in other flows
        dispatch(fetchAnnualTaxFilingFormsIfNeeded(true)),
        dispatch(fetchAnnualTaxFilingsIfNeeded(true)),
      ])
      setFetching(false)
    }
    batchFetch()
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchUserEoyReviewProgress(taxYear))
  }, [dispatch, taxYear])

  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const hasAnnualTaxesPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.annualTaxes
  )
  const annualTaxFiling = useReselector(
    getAnnualTaxFilingForYearSelector,
    taxYear
  )
  const lateJoinerOptedOutDuringSignUp = useReselector(
    selectLaterJoinerOptedOutDuringSignup
  )

  const tskSubmitted = annualTaxFiling?.pretaxSurveySubmittedAt
  const isEligibleForTaxServices = useReselector(
    selectIsEligibleForCurrentYearTaxServices
  )
  const annualTaxNotAvailable = useReselector(selectAnnualTaxNotAvailable)

  const disable2024AnnualTax = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.disable2024AnnualTax,
    false
  )
  const isTaxSeasonOpen = useIsTaxSeasonOpen()

  if (fetching) {
    return AnnualTaxOverviewPage.loading
  }

  // No-permission users (Heard Lite and others): evaluate before the global
  // tax-season branch so they get an upsell screen instead of nextYearTaxCenter.
  if (!hasAnnualTaxesPermission) {
    // An upgrade today can no longer buy this year's filing.
    if (
      disable2024AnnualTax ||
      !isEligibleForTaxServices ||
      annualTaxNotAvailable
    ) {
      return AnnualTaxOverviewPage.upgradeToPremiumOffSeason
    }
    return AnnualTaxOverviewPage.upgradeToPremium
  }

  // Permitted users only below this line.
  if (disable2024AnnualTax || !isEligibleForTaxServices) {
    return AnnualTaxOverviewPage.nextYearTaxCenter
  }

  // Check if user is disqualified from annual taxes
  if (annualTaxFiling?.disqualifiedAt) {
    return AnnualTaxOverviewPage.disqualified
  }
  if (lateJoinerOptedOutDuringSignUp && annualTaxFiling?.optedOutAt !== null) {
    return AnnualTaxOverviewPage.lateJoinerOptedOut
  }
  if (tskSubmitted) {
    return AnnualTaxOverviewPage.yearEndHome
  }
  if (isInUnservicedFreeTrial) {
    return AnnualTaxOverviewPage.freeTrialAnnualTaxPreview
  }
  // Wait for both flags so neither screen flashes before the other.
  if (isTaxSeasonOpen === null) {
    return AnnualTaxOverviewPage.loading
  }
  if (!isTaxSeasonOpen) {
    return AnnualTaxOverviewPage.taxSeasonNotOpen
  }
  return AnnualTaxOverviewPage.annualTaxTskEntryPoint
}
